import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import { AppDispatch, RootState } from "../store";
import { setAIReview, setPatient, touchPatientUpdatedAt } from "../store/actions";
import { fetchPinnedReviews, fetchPinnedReviewCount } from "../store/middleware";
import { updateAIReview } from "../api/aiReviewService";
import { ToastManager } from "../components/organisms/CommonUI/ToastPopup/ToastManager";
import { log, LogLevel } from "../utils/LogUtil";

export const useAIReviewPin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const { patients, reviews, pinnedReviews } = useSelector((state: RootState) => state.data);
  const [isLoading, setIsLoading] = useState(false);

  const togglePin = async (reviewId: number) => {
    setIsLoading(true);
    try {
      // 1. 현재 review 가져오기
      const currentReview = reviews[reviewId];
      if (!currentReview) {
        log(LogLevel.ERROR, "useAIReviewPin:togglePin:Review not found", reviewId);
        return;
      }

      // 2. pinnedAt 토글
      const newPinnedAt = currentReview.pinnedAt ? null : new Date();

      log(LogLevel.INFO, "useAIReviewPin:togglePin:Toggling pin status", {
        reviewId,
        currentPinnedAt: currentReview.pinnedAt,
        newPinnedAt,
      });

      // 3. API 호출
      const result = await updateAIReview(reviewId, {
        pinnedAt: currentReview.pinnedAt ? null : 1,
      });

      if (result.error === 0 && result.data) {
        // 4. Redux store 업데이트
        dispatch(setAIReview({ id: reviewId, pinnedAt: newPinnedAt }));

        // 4-1. 대시보드가 로드된 상태면 핀 목록/카운트 재조회 (서버가 source of truth)
        if (pinnedReviews !== null) {
          dispatch(fetchPinnedReviews());
          dispatch(fetchPinnedReviewCount());
        }

        // 4-2. 보고서 관리 페이지의 React Query 캐시 무효화
        // (해당 쿼리가 활성화되지 않은 상태면 noop이라 항상 호출해도 안전)
        queryClient.invalidateQueries(["aiReviewList"]);
        queryClient.invalidateQueries(["aiReviewCount"]);

        // 5. 환자의 aiReviews 재정렬 (patientId가 있는 경우)
        if (currentReview.patientId) {
          const patient = patients[currentReview.patientId];
          if (patient?.aiReviews) {
            // 업데이트된 reviews 상태로 정렬
            const updatedReviews = {
              ...reviews,
              [reviewId]: { ...reviews[reviewId], pinnedAt: newPinnedAt },
            };

            const sortedReviews = [...patient.aiReviews].sort((a, b) => {
              const aReview = updatedReviews[a.id];
              const bReview = updatedReviews[b.id];
              // pinnedAt 우선, 그 다음 id 역순
              const aPinned = aReview?.pinnedAt ? 1 : 0;
              const bPinned = bReview?.pinnedAt ? 1 : 0;

              if (aPinned !== bPinned) return bPinned - aPinned;
              if (aReview?.pinnedAt && bReview?.pinnedAt) {
                return bReview.pinnedAt < aReview.pinnedAt ? 1 : -1;
              }
              return b.id - a.id;
            });

            log(LogLevel.INFO, "useAIReviewPin:togglePin:Sorted reviews", sortedReviews);
            dispatch(
              setPatient({
                ...patient,
                aiReviews: sortedReviews,
              })
            );
            dispatch(touchPatientUpdatedAt(currentReview.patientId));
          }
        }

        // 6. 성공 메시지
        ToastManager.show({
          content: newPinnedAt ? "보고서가 고정되었습니다." : "보고서 고정이 해제되었습니다.",
          duration: 2000,
        });

        log(LogLevel.INFO, "useAIReviewPin:togglePin:Pin status updated successfully", {
          reviewId,
          pinnedAt: result.data.pinnedAt,
        });
      } else {
        throw new Error(result.message || "useAIReviewPin:togglePin:Failed to update pin status");
      }
    } catch (error) {
      log(LogLevel.ERROR, "useAIReviewPin:togglePin:Failed to toggle pin", error);
      ToastManager.show({
        content: "고정 상태 변경에 실패했습니다.",
        duration: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { togglePin, isLoading };
};
